import { z } from 'zod';
import { ApprovalStatus, BookingStatus, LessonType, Locale, PaymentStatus, SkillLevel, UserRole, } from './enums.js';
import { isoDateSchema, subjectPricesSchema } from './schemas.js';
export const AdminBookingKind = {
    LESSON: 'LESSON',
    ROOM: 'ROOM',
};
export const AdminBookingScope = {
    ALL: 'ALL',
    LESSONS: 'LESSONS',
    ROOMS: 'ROOMS',
};
export const AdminBookingState = {
    HOLD: 'HOLD',
    CONFIRMED: 'CONFIRMED',
    ATTENDED: 'ATTENDED',
    NO_SHOW: 'NO_SHOW',
    CANCELLED: 'CANCELLED',
};
export const AdminPaymentState = {
    PAID: 'PAID',
    UNPAID: 'UNPAID',
    FREE_TRIAL: 'FREE_TRIAL',
    REFUND_DUE: 'REFUND_DUE',
    REFUNDED: 'REFUNDED',
};
export const AdminAttentionKind = {
    HOLDS_EXPIRING: 'HOLDS_EXPIRING',
    UNPAID_PAST: 'UNPAID_PAST',
    NO_WORKING_HOURS: 'NO_WORKING_HOURS',
    UNHANDLED_MESSAGES: 'UNHANDLED_MESSAGES',
};
export const AdminResourceKind = {
    TEACHER: 'TEACHER',
    ROOM: 'ROOM',
};
export const AdminTimelineKind = {
    LESSON: 'LESSON',
    TRIAL: 'TRIAL',
    ROOM: 'ROOM',
    CANCELLED: 'CANCELLED',
    BLOCKED: 'BLOCKED',
};
export const AdminStudentState = {
    PENDING: 'PENDING',
    ACTIVE: 'ACTIVE',
    TRIAL_BOOKED: 'TRIAL_BOOKED',
    DORMANT: 'DORMANT',
    UNVERIFIED: 'UNVERIFIED',
    REJECTED: 'REJECTED',
    DEACTIVATED: 'DEACTIVATED',
};
export const AdminInboxScope = {
    UNHANDLED: 'UNHANDLED',
    HANDLED: 'HANDLED',
    ALL: 'ALL',
};
export const AdminContentTab = {
    TESTIMONIALS: 'TESTIMONIALS',
    FAQ: 'FAQ',
    COMMUNITY: 'COMMUNITY',
    HOMEPAGE: 'HOMEPAGE',
};
export const AdminClosureKind = {
    DATE: 'DATE',
    RANGE: 'RANGE',
    WEEKLY: 'WEEKLY',
};
export const AdminPartOfDay = {
    MORNING: 'MORNING',
    AFTERNOON: 'AFTERNOON',
    EVENING: 'EVENING',
};
export const AdminExceptionKind = {
    UNAVAILABLE: 'UNAVAILABLE',
    EXTRA_HOURS: 'EXTRA_HOURS',
    ACADEMY_CLOSURE: 'ACADEMY_CLOSURE',
};
export const ADMIN_DESTINATIONS = [
    'dashboard',
    'bookings',
    'schedule',
    'teachers',
    'students',
    'rooms',
    'subjects',
    'payments',
    'content',
    'inbox',
    'settings',
];
export const ADMIN_PAGE_SIZE = 25;
export const PHOTO_MAX_BYTES = 2 * 1024 * 1024;
export const PHOTO_MAX_SIDE = 4096;
export const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const PHOTO_MIN_SIZE = {
    teacher: { width: 320, height: 380 },
    room: { width: 420, height: 236 },
};
export const ROOM_PHOTO_LIMIT = 8;
export const ADMIN_GRID_START_MINUTE = 9 * 60;
export const ADMIN_GRID_END_MINUTE = 23 * 60;
export const AdminPriceEntity = {
    ROOM: 'ROOM',
    TEACHER: 'TEACHER',
    TEACHER_SUBJECT: 'TEACHER_SUBJECT',
    ACADEMY: 'ACADEMY',
};
export const AdminPriceField = {
    HOURLY: 'HOURLY',
    TRIAL: 'TRIAL',
};
export const adminPersonSchema = z.object({
    id: z.string().nullable(),
    fullName: z.string(),
    email: z.string(),
    avatarUrl: z.string().nullable(),
});
export const adminMinuteSchema = z
    .number()
    .int()
    .min(0)
    .max(24 * 60);
export const adminMoneySchema = z.object({
    amountMinor: z.number().int(),
    currency: z.string(),
});
export const adminStatSchema = z.object({
    key: z.string(),
    value: z.number(),
    currency: z.string().nullable(),
    isPercent: z.boolean(),
});
export const adminAttentionSchema = z.object({
    kind: z.nativeEnum(AdminAttentionKind),
    count: z.number().int(),
    minutes: z.number().int().nullable(),
    names: z.array(z.string()),
    more: z.number().int(),
    since: z.string().nullable(),
    href: z.string(),
});
export const adminScheduleRowSchema = z.object({
    reference: z.string(),
    kind: z.nativeEnum(AdminBookingKind),
    state: z.nativeEnum(AdminBookingState),
    payment: z.nativeEnum(AdminPaymentState),
    timeline: z.nativeEnum(AdminTimelineKind),
    startsAt: z.string(),
    endsAt: z.string(),
    startMinute: z.number().int(),
    durationMinutes: z.number().int(),
    person: adminPersonSchema.nullable(),
    subjectName: z.string().nullable(),
    subjectSlug: z.string().nullable(),
    roomName: z.string().nullable(),
    teacherName: z.string().nullable(),
    studio: z.string().nullable(),
    isTrial: z.boolean(),
    cancelledAt: z.string().nullable(),
    holdsUntil: z.string().nullable(),
    money: adminMoneySchema,
    isFirstLesson: z.boolean(),
});
export const adminInboxPreviewSchema = z.object({
    id: z.string(),
    fullName: z.string(),
    topic: z.string(),
    excerpt: z.string(),
    receivedAt: z.string(),
    handled: z.boolean(),
});
export const adminRoomLoadSchema = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    subtitle: z.string().nullable(),
    bookedMinutes: z.number().int(),
    openMinutes: z.number().int(),
});
export const adminMonthSummarySchema = z.object({
    monthKey: z.string(),
    collectedMinor: z.number().int(),
    outstandingMinor: z.number().int(),
    refundedMinor: z.number().int(),
    heldMinor: z.number().int(),
    lessonCount: z.number().int(),
    roomBookingCount: z.number().int(),
    currency: z.string(),
});
export const adminDashboardSchema = z.object({
    firstName: z.string(),
    partOfDay: z.nativeEnum(AdminPartOfDay),
    todayKey: z.string(),
    lessonsToday: z.number().int(),
    roomBookingsToday: z.number().int(),
    holdsExpiring: z.number().int(),
    stats: z.array(adminStatSchema),
    attention: z.array(adminAttentionSchema),
    today: z.array(adminScheduleRowSchema),
    todayTotal: z.number().int(),
    todayRemaining: z.number().int(),
    unhandled: z.array(adminInboxPreviewSchema),
    unhandledCount: z.number().int(),
    month: adminMonthSummarySchema,
    rooms: z.array(adminRoomLoadSchema),
});
export const adminBookingsQuerySchema = z.object({
    scope: z.nativeEnum(AdminBookingScope).default(AdminBookingScope.ALL),
    q: z.string().trim().max(120).optional(),
    state: z.nativeEnum(AdminBookingState).optional(),
    teacher: z.string().trim().max(120).optional(),
    month: z
        .string()
        .regex(/^\d{4}-\d{2}$/, 'Expected a YYYY-MM month')
        .optional(),
    page: z.coerce.number().int().min(1).default(1),
});
export const adminBookingRowSchema = adminScheduleRowSchema.extend({
    contactEmail: z.string(),
});
export const adminBookingsPageSchema = z.object({
    rows: z.array(adminBookingRowSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageCount: z.number().int(),
    from: z.number().int(),
    to: z.number().int(),
    teachers: z.array(z.object({ slug: z.string(), fullName: z.string() })),
    monthKey: z.string(),
});
export const adminBookingEventSchema = z.object({
    key: z.string(),
    at: z.string().nullable(),
    scheduled: z.boolean(),
    detail: z.record(z.string(), z.string()).nullable(),
});
export const adminBookingDetailSchema = z.object({
    reference: z.string(),
    kind: z.nativeEnum(AdminBookingKind),
    state: z.nativeEnum(AdminBookingState),
    payment: z.nativeEnum(AdminPaymentState),
    type: z.nativeEnum(LessonType).nullable(),
    startsAt: z.string(),
    endsAt: z.string(),
    durationMinutes: z.number().int(),
    createdAt: z.string(),
    stateSince: z.string(),
    subjectName: z.string().nullable(),
    subjectSlug: z.string().nullable(),
    teacherName: z.string().nullable(),
    teacherSlug: z.string().nullable(),
    roomName: z.string().nullable(),
    studio: z.string().nullable(),
    money: adminMoneySchema,
    slotKey: z.string().nullable(),
    requestId: z.string().nullable(),
    contact: z.object({
        fullName: z.string(),
        email: z.string(),
        phone: z.string(),
        experience: z.nativeEnum(SkillLevel).nullable(),
        locale: z.nativeEnum(Locale),
        whatsappOptIn: z.boolean(),
        notes: z.string().nullable(),
    }),
    history: z.array(adminBookingEventSchema),
    student: z
        .object({
        id: z.string(),
        fullName: z.string(),
        email: z.string(),
        avatarUrl: z.string().nullable(),
        lessonsTaken: z.number().int(),
        since: z.string(),
        lastLoginAt: z.string().nullable(),
    })
        .nullable(),
    hasHappened: z.boolean(),
});
export const adminSetBookingStateSchema = z.object({
    state: z.enum([
        AdminBookingState.CONFIRMED,
        AdminBookingState.ATTENDED,
        AdminBookingState.NO_SHOW,
    ]),
});
export const adminCancelBookingSchema = z.object({
    reason: z.string().trim().max(400).optional(),
});
export const adminSetPaymentSchema = z.object({
    paymentStatus: z.nativeEnum(PaymentStatus),
});
export const adminMoveBookingSchema = z.object({
    date: isoDateSchema,
    startMinute: adminMinuteSchema,
});
export const adminScheduleQuerySchema = z.object({
    date: isoDateSchema.optional(),
    view: z.nativeEnum(AdminBookingScope).default(AdminBookingScope.ALL),
    resource: z.string().trim().max(120).optional(),
});
export const adminTimelineBlockSchema = z.object({
    reference: z.string().nullable(),
    kind: z.nativeEnum(AdminTimelineKind),
    title: z.string(),
    subjectName: z.string().nullable(),
    subjectSlug: z.string().nullable(),
    durationMinutes: z.number().int(),
    state: z.nativeEnum(AdminBookingState).nullable(),
    payment: z.nativeEnum(AdminPaymentState).nullable(),
    startMinute: z.number().int(),
    endMinute: z.number().int(),
    cancelledAt: z.string().nullable(),
    unregistered: z.boolean(),
});
export const adminResourceColumnSchema = z.object({
    id: z.string(),
    slug: z.string(),
    kind: z.nativeEnum(AdminResourceKind),
    name: z.string(),
    subtitle: z.string().nullable(),
    subjectSlug: z.string().nullable(),
    windows: z.array(z.object({ startMinute: z.number().int(), endMinute: z.number().int() })),
    blocks: z.array(adminTimelineBlockSchema),
});
export const adminScheduleDaySchema = z.object({
    dateKey: z.string(),
    weekStartKey: z.string(),
    days: z.array(z.object({
        dateKey: z.string(),
        closed: z.boolean(),
        isToday: z.boolean(),
    })),
    closed: z.boolean(),
    closureReason: z.string().nullable(),
    resources: z.array(adminResourceColumnSchema),
    gridStartMinute: z.number().int(),
    gridEndMinute: z.number().int(),
    nowMinute: z.number().int().nullable(),
    options: z.array(z.object({ slug: z.string(), name: z.string(), kind: z.nativeEnum(AdminResourceKind) })),
});
export const adminTeachersQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    subject: z.string().trim().max(60).optional(),
    publishedOnly: z.coerce.boolean().optional(),
    page: z.coerce.number().int().min(1).default(1),
});
export const adminTeacherRowSchema = z.object({
    id: z.string(),
    slug: z.string(),
    fullName: z.string(),
    email: z.string(),
    avatarUrl: z.string().nullable(),
    subjects: z.array(z.object({ slug: z.string(), name: z.string() })),
    hourlyRateMinor: z.number().int(),
    trialPriceMinor: z.number().int(),
    currency: z.string(),
    lessonsThisWeek: z.number().int(),
    availableMinutes: z.number().int(),
    neverTaught: z.boolean(),
    published: z.boolean(),
    featured: z.boolean(),
});
export const adminTeachersPageSchema = z.object({
    rows: z.array(adminTeacherRowSchema),
    total: z.number().int(),
    shown: z.number().int(),
    publishedCount: z.number().int(),
    featuredCount: z.number().int(),
    page: z.number().int(),
    pageCount: z.number().int(),
    subjects: z.array(z.object({ slug: z.string(), name: z.string() })),
});
export const adminExceptionSchema = z.object({
    id: z.string(),
    dateKey: z.string(),
    kind: z.nativeEnum(AdminExceptionKind),
    startMinute: z.number().int().nullable(),
    endMinute: z.number().int().nullable(),
    reason: z.string().nullable(),
    locked: z.boolean(),
});
export const adminDayWindowsSchema = z.object({
    dayOfWeek: z.number().int().min(0).max(6),
    windows: z
        .array(z.object({
        startMinute: adminMinuteSchema,
        endMinute: adminMinuteSchema,
    }))
        .max(6),
});
export const adminWorkingHoursSchema = z.object({
    days: z.array(adminDayWindowsSchema).length(7),
});
export const adminUpdateWorkingHoursSchema = adminWorkingHoursSchema.superRefine((value, ctx) => {
    const seen = new Set();
    value.days.forEach((day, dayIndex) => {
        if (seen.has(day.dayOfWeek)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['days', dayIndex, 'dayOfWeek'],
                message: 'Each weekday may appear once',
            });
        }
        seen.add(day.dayOfWeek);
        const sorted = [...day.windows].sort((a, b) => a.startMinute - b.startMinute);
        sorted.forEach((window, index) => {
            if (window.endMinute <= window.startMinute) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['days', dayIndex, 'windows', index, 'endMinute'],
                    message: 'The end of a window has to come after its start',
                });
            }
            const previous = sorted[index - 1];
            if (previous && window.startMinute < previous.endMinute) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['days', dayIndex, 'windows', index, 'startMinute'],
                    message: 'Windows on one day cannot overlap',
                });
            }
        });
    });
});
export const adminPriceChangeSchema = z.object({
    id: z.string(),
    entity: z.nativeEnum(AdminPriceEntity),
    price: z.nativeEnum(AdminPriceField),
    entityKey: z.string(),
    entityName: z.string(),
    subjectName: z.string().nullable(),
    subjectSlug: z.string().nullable(),
    fromMinor: z.number().int().nullable(),
    toMinor: z.number().int().nullable(),
    currency: z.string(),
    actorName: z.string(),
    changedAt: z.string(),
});
export const adminTeacherSubjectRateSchema = z.object({
    slug: z.string(),
    name: z.string(),
    rateMinor: z.number().int().nullable(),
});
export const adminTeacherDetailSchema = z.object({
    id: z.string(),
    slug: z.string(),
    fullName: z.string(),
    email: z.string(),
    avatarUrl: z.string().nullable(),
    photoUrl: z.string().nullable(),
    headline: z.string(),
    yearsTeaching: z.number().int(),
    languages: z.array(z.string()),
    published: z.boolean(),
    featured: z.boolean(),
    teachesAtHome: z.boolean(),
    hourlyRateMinor: z.number().int(),
    trialPriceMinor: z.number().int(),
    currency: z.string(),
    workingHours: adminWorkingHoursSchema,
    weeklyMinutes: z.number().int(),
    workingDays: z.number().int(),
    exceptions: z.array(adminExceptionSchema),
    closedWeekdays: z.array(z.number().int()),
    bookedInsideHours: z.number().int(),
    subjects: z.array(adminTeacherSubjectRateSchema),
    priceChanges: z.array(adminPriceChangeSchema),
});
export const adminTeacherFlagsSchema = z.object({
    published: z.boolean(),
    featured: z.boolean(),
    teachesAtHome: z.boolean(),
});
export const adminTeacherRatesSchema = z.object({
    hourlyRateMinor: z.number().int().min(0).max(1_000_000),
    trialPriceMinor: z.number().int().min(0).max(1_000_000),
});
export const adminCreateExceptionSchema = z
    .object({
    date: isoDateSchema,
    kind: z.enum([AdminExceptionKind.UNAVAILABLE, AdminExceptionKind.EXTRA_HOURS]),
    startMinute: adminMinuteSchema.optional(),
    endMinute: adminMinuteSchema.optional(),
    reason: z.string().trim().max(200).optional(),
})
    .superRefine((value, ctx) => {
    const partial = value.startMinute !== undefined || value.endMinute !== undefined;
    if (value.kind === AdminExceptionKind.EXTRA_HOURS && !partial) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['startMinute'],
            message: 'Extra hours need a start and an end',
        });
        return;
    }
    if (partial) {
        if (value.startMinute === undefined || value.endMinute === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['endMinute'],
                message: 'Give both a start and an end, or neither for the whole day',
            });
        }
        else if (value.endMinute <= value.startMinute) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['endMinute'],
                message: 'The end has to come after the start',
            });
        }
    }
});
export const adminInviteTeacherSchema = z.object({
    fullName: z.string().trim().min(2).max(120),
    email: z.string().trim().toLowerCase().email('Enter a valid email address'),
    headline: z.string().trim().max(160).optional(),
    subjectSlug: z.string().trim().max(60).optional(),
});
export const adminStudentsQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    state: z.nativeEnum(AdminStudentState).optional(),
    subject: z.string().trim().max(60).optional(),
    page: z.coerce.number().int().min(1).default(1),
});
export const adminStudentRowSchema = z.object({
    id: z.string(),
    fullName: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    state: z.nativeEnum(AdminStudentState),
    lessonsTaken: z.number().int(),
    upcoming: z.number().int(),
    trialUpcoming: z.boolean(),
    roomsOnly: z.boolean(),
    createdByStaff: z.boolean(),
    lastLoginAt: z.string().nullable(),
    approvalStatus: z.nativeEnum(ApprovalStatus),
    signedUpAt: z.string(),
});
export const adminStudentsPageSchema = z.object({
    rows: z.array(adminStudentRowSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageCount: z.number().int(),
    from: z.number().int(),
    to: z.number().int(),
    subjects: z.array(z.object({ slug: z.string(), name: z.string() })),
    pendingCount: z.number().int(),
});
export const adminCreateStudentSchema = z.object({
    fullName: z.string().trim().min(2).max(120),
    email: z.string().trim().toLowerCase().email('Enter a valid email address'),
    phone: z.string().trim().max(20).optional(),
    locale: z.nativeEnum(Locale).optional(),
});
export const adminSetActiveSchema = z.object({ active: z.boolean() });
export const adminApprovalDecisionSchema = z.object({
    approve: z.boolean(),
    note: z.string().trim().max(300).optional(),
});
export const adminRoomRowSchema = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    description: z.string(),
    capacity: z.number().int(),
    equipment: z.array(z.string()),
    openMinute: z.number().int(),
    closeMinute: z.number().int(),
    hourlyRateMinor: z.number().int(),
    currency: z.string(),
    published: z.boolean(),
    photos: z.array(z.string()),
    bookedMinutes: z.number().int(),
    openMinutes: z.number().int(),
});
export const adminRoomsPageSchema = z.object({
    rows: z.array(adminRoomRowSchema),
    stats: z.array(adminStatSchema),
    publishedCount: z.number().int(),
    hiddenNames: z.array(z.string()),
});
export const adminRoomRatesSchema = z.object({
    hourlyRateMinor: z.number().int().min(0).max(1_000_000),
});
export const adminRoomPhotosSchema = z.object({
    photos: z
        .array(z.string().trim().min(1).max(300))
        .max(ROOM_PHOTO_LIMIT)
        .refine((photos) => new Set(photos).size === photos.length, 'Each photo can appear once'),
});
export const adminSetPublishedSchema = z.object({ published: z.boolean() });
export const adminSubjectRowSchema = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    tagline: z.string(),
    icon: z.string(),
    levelLine: z.string(),
    sortOrder: z.number().int(),
    published: z.boolean(),
    teacherCount: z.number().int(),
    publishedTeacherCount: z.number().int(),
    lessonCount: z.number().int(),
    prices: subjectPricesSchema,
});
export const adminSubjectPricesSchema = subjectPricesSchema.superRefine((value, ctx) => {
    const { monthlyMinor, monthlyDiscountMinor } = value;
    if (monthlyDiscountMinor !== null &&
        (monthlyMinor === null || monthlyDiscountMinor >= monthlyMinor)) {
        ctx.addIssue({
            code: 'custom',
            path: ['monthlyDiscountMinor'],
            message: 'The discount has to be below the monthly price',
        });
    }
});
export const adminSubjectsPageSchema = z.object({
    rows: z.array(adminSubjectRowSchema),
    publishedCount: z.number().int(),
});
export const adminReorderSubjectsSchema = z.object({
    slugs: z.array(z.string().trim().min(1).max(60)).min(1).max(60),
});
export const adminSubjectRateSchema = z.object({
    subjectSlug: z.string().trim().min(1).max(60),
    rateMinor: z.number().int().min(0).max(1_000_000).nullable(),
});
export const adminPriceImpactQuerySchema = z
    .object({
    kind: z.nativeEnum(AdminPriceEntity),
    key: z.string().trim().min(1).max(120).optional(),
    subjectSlug: z.string().trim().min(1).max(60).optional(),
    toMinor: z.coerce.number().int().min(0).max(1_000_000).optional(),
})
    .superRefine((q, ctx) => {
    if (q.kind !== AdminPriceEntity.ACADEMY && q.key === undefined) {
        ctx.addIssue({ code: 'custom', path: ['key'], message: 'Required' });
    }
    if (q.kind === AdminPriceEntity.TEACHER_SUBJECT && q.subjectSlug === undefined) {
        ctx.addIssue({ code: 'custom', path: ['subjectSlug'], message: 'Required' });
    }
    if (q.kind !== AdminPriceEntity.TEACHER_SUBJECT && q.toMinor === undefined) {
        ctx.addIssue({ code: 'custom', path: ['toMinor'], message: 'Required' });
    }
});
export const adminPriceImpactSchema = z.object({
    futureBookings: z.number().int(),
    fromMinor: z.number().int(),
    toMinor: z.number().int(),
    currency: z.string(),
});
export const adminPaymentsQuerySchema = z.object({
    state: z.nativeEnum(AdminPaymentState).optional(),
    month: z
        .string()
        .regex(/^\d{4}-\d{2}$/, 'Expected a YYYY-MM month')
        .optional(),
    page: z.coerce.number().int().min(1).default(1),
});
export const adminPaymentRowSchema = z.object({
    reference: z.string(),
    kind: z.nativeEnum(AdminBookingKind),
    dateKey: z.string(),
    studentName: z.string(),
    subjectName: z.string().nullable(),
    subjectSlug: z.string().nullable(),
    roomName: z.string().nullable(),
    hours: z.number().nullable(),
    cancelled: z.boolean(),
    isTrial: z.boolean(),
    money: adminMoneySchema,
    state: z.nativeEnum(AdminPaymentState),
    receiptUrl: z.string().nullable(),
});
export const adminPaymentsPageSchema = z.object({
    rows: z.array(adminPaymentRowSchema),
    stats: z.array(adminStatSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageCount: z.number().int(),
    shown: z.number().int(),
    monthKey: z.string(),
});
export const adminTestimonialSchema = z.object({
    id: z.string(),
    quote: z.string(),
    authorName: z.string(),
    authorContext: z.string(),
    subjectSlug: z.string().nullable(),
    teacherId: z.string().nullable(),
    teacherName: z.string().nullable(),
    rating: z.number().int().nullable(),
    published: z.boolean(),
    sortOrder: z.number().int(),
});
export const adminSaveTestimonialSchema = z.object({
    quote: z.string().trim().min(10).max(600),
    authorName: z.string().trim().min(2).max(120),
    authorContext: z.string().trim().max(160),
    subjectSlug: z.string().trim().max(60).nullable().optional(),
    teacherId: z.string().trim().max(60).nullable().optional(),
    rating: z.number().int().min(1).max(5).nullable().optional(),
    published: z.boolean(),
});
export const adminFaqItemSchema = z.object({
    id: z.string(),
    question: z.string(),
    answer: z.string(),
    category: z.string(),
    published: z.boolean(),
    sortOrder: z.number().int(),
});
export const adminSaveFaqSchema = z.object({
    question: z.string().trim().min(6).max(240),
    answer: z.string().trim().min(10).max(2000),
    category: z.string().trim().min(2).max(60),
    published: z.boolean(),
});
export const adminCommunityRowSchema = z.object({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    authorName: z.string(),
    authorRole: z.nativeEnum(UserRole),
    topic: z.string(),
    answerCount: z.number().int(),
    helpfulCount: z.number().int(),
    publishedAt: z.string(),
    published: z.boolean(),
});
export const adminHomepageSchema = z.object({
    featuredTeachers: z.array(z.object({
        id: z.string(),
        slug: z.string(),
        fullName: z.string(),
        subjects: z.array(z.object({ slug: z.string(), name: z.string() })),
        published: z.boolean(),
        featured: z.boolean(),
        teachesAtHome: z.boolean(),
    })),
    publishedSubjects: z.number().int(),
    publishedTestimonials: z.number().int(),
    publishedFaq: z.number().int(),
    publishedRooms: z.number().int(),
});
export const adminInboxQuerySchema = z.object({
    scope: z.nativeEnum(AdminInboxScope).default(AdminInboxScope.UNHANDLED),
    q: z.string().trim().max(120).optional(),
});
export const adminMessageSchema = z.object({
    id: z.string(),
    fullName: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    topic: z.string(),
    message: z.string(),
    excerpt: z.string(),
    locale: z.nativeEnum(Locale),
    receivedAt: z.string(),
    handled: z.boolean(),
    handledAt: z.string().nullable(),
    internalNote: z.string().nullable(),
});
export const adminInboxPageSchema = z.object({
    rows: z.array(adminMessageSchema),
    unhandledCount: z.number().int(),
    total: z.number().int(),
});
export const adminInboxNoteSchema = z.object({
    internalNote: z.string().trim().max(2000),
});
export const adminSetHandledSchema = z.object({ handled: z.boolean() });
export const adminAcademySchema = z
    .object({
    name: z.string().trim().min(2).max(120),
    phone: z.string().trim().min(6).max(30),
    email: z.string().trim().toLowerCase().email('Enter a valid email address'),
    address: z.string().trim().min(6).max(240),
    defaultLocale: z.nativeEnum(Locale),
    openMinute: z.number().int().min(0).max(1439),
    closeMinute: z.number().int().min(0).max(1439),
})
    .refine((academy) => academy.closeMinute > academy.openMinute, {
    path: ['closeMinute'],
    message: 'Closing time must be after opening time',
});
export const adminPolicySchema = z.object({
    holdMinutes: z.number().int().min(1).max(120),
    trialMinutes: z.number().int().min(15).max(120),
    cancellationHours: z.number().int().min(0).max(168),
    minimumNoticeHours: z.number().int().min(0).max(168),
    bookingHorizonDays: z.number().int().min(1).max(365),
    trialPriceMinor: z.number().int().min(0).max(1_000_000),
});
export const adminClosureSchema = z.object({
    id: z.string(),
    kind: z.nativeEnum(AdminClosureKind),
    startDateKey: z.string().nullable(),
    endDateKey: z.string().nullable(),
    weekday: z.number().int().nullable(),
    reason: z.string().nullable(),
});
export const adminCreateClosureSchema = z
    .object({
    kind: z.nativeEnum(AdminClosureKind),
    startDate: isoDateSchema.optional(),
    endDate: isoDateSchema.optional(),
    weekday: z.number().int().min(0).max(6).optional(),
    reason: z.string().trim().max(120).optional(),
})
    .superRefine((value, ctx) => {
    if (value.kind === AdminClosureKind.WEEKLY) {
        if (value.weekday === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['weekday'],
                message: 'Pick the day of the week',
            });
        }
        return;
    }
    if (!value.startDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['startDate'],
            message: 'Pick a date',
        });
        return;
    }
    if (value.kind === AdminClosureKind.RANGE) {
        if (!value.endDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['endDate'],
                message: 'Pick the last day of the range',
            });
        }
        else if (value.endDate < value.startDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['endDate'],
                message: 'The range has to end after it starts',
            });
        }
    }
});
export const adminStaffSchema = z.object({
    id: z.string(),
    fullName: z.string(),
    email: z.string(),
    avatarUrl: z.string().nullable(),
    role: z.nativeEnum(UserRole),
    lastLoginAt: z.string().nullable(),
    isSelf: z.boolean(),
    pending: z.boolean(),
});
export const adminLegalDraftSchema = z.object({ legalDraft: z.boolean() });
export const adminSettingsSchema = z.object({
    academy: adminAcademySchema,
    legalDraft: z.boolean(),
    policy: adminPolicySchema,
    timeZone: z.string(),
    closures: z.array(adminClosureSchema),
    staff: z.array(adminStaffSchema),
});
export const adminInviteStaffSchema = z.object({
    fullName: z.string().trim().min(2).max(120),
    email: z.string().trim().toLowerCase().email('Enter a valid email address'),
    role: z.enum([UserRole.TEACHER, UserRole.ADMIN]),
});
export const adminSetStaffRoleSchema = z.object({
    role: z.enum([UserRole.TEACHER, UserRole.ADMIN, UserRole.STUDENT]),
});
export function bookingStateOf(status) {
    switch (status) {
        case BookingStatus.PENDING:
            return AdminBookingState.HOLD;
        case BookingStatus.ATTENDED:
            return AdminBookingState.ATTENDED;
        case BookingStatus.NO_SHOW:
            return AdminBookingState.NO_SHOW;
        case BookingStatus.CANCELLED:
            return AdminBookingState.CANCELLED;
        default:
            return AdminBookingState.CONFIRMED;
    }
}
export function bookingStatusesFor(state) {
    switch (state) {
        case AdminBookingState.HOLD:
            return [BookingStatus.PENDING];
        case AdminBookingState.ATTENDED:
            return [BookingStatus.ATTENDED];
        case AdminBookingState.NO_SHOW:
            return [BookingStatus.NO_SHOW];
        case AdminBookingState.CANCELLED:
            return [BookingStatus.CANCELLED];
        default:
            return [BookingStatus.CONFIRMED];
    }
}
export function paymentStateOf(row) {
    if (row.paymentStatus === PaymentStatus.REFUNDED)
        return AdminPaymentState.REFUNDED;
    if (row.status === BookingStatus.CANCELLED) {
        return row.paymentStatus === PaymentStatus.PAID
            ? AdminPaymentState.REFUND_DUE
            : AdminPaymentState.UNPAID;
    }
    if (row.paymentStatus === PaymentStatus.PAID)
        return AdminPaymentState.PAID;
    return row.priceMinor === 0 ? AdminPaymentState.FREE_TRIAL : AdminPaymentState.UNPAID;
}
export function timelineKindOf(row) {
    if (row.status === BookingStatus.CANCELLED)
        return AdminTimelineKind.CANCELLED;
    if (row.kind === AdminBookingKind.ROOM)
        return AdminTimelineKind.ROOM;
    return row.isTrial ? AdminTimelineKind.TRIAL : AdminTimelineKind.LESSON;
}
//# sourceMappingURL=admin.js.map